import { comparer, reaction } from 'mobx'
import type { BootEvent, BootPhase } from './boot-phase'

export type SessionBridgeDeps = {
    /** aqe's session state; only `sessionExpired` is read. */
    sessionState: { sessionExpired: boolean }
    /** Pufferfish's app state; `isLoggingOut` is set when a logout starts. */
    appState: { isLoggingOut: boolean }
    /** The boot phase this bridge serves. Create a new bridge when the phase changes. */
    phase: BootPhase['phase']
    send: (event: BootEvent) => void
}

const lastLoggingOut = new WeakMap<object, boolean>()

/**
 * Reports the session facts that arrive outside boot to the boot machine: the session expiring or
 * being restored, and a logout starting or failing. It reads the current state once on creation, so a change
 * made while an earlier phase was running is not lost. The machine ignores an event that does not
 * apply, and this also checks the phase, so a stale bridge cannot move boot. A logout that fails is
 * reported by whichever bridge sees it end; `logoutFailed` only moves the machine out of `unauthenticated`.
 *
 * Cross-tab logout arrives as `sessionExpired = true` (aqe sets it), so it reports as an expiry.
 * Returns a disposer.
 */
export function bridgeSessionToBoot({ sessionState, appState, phase, send }: SessionBridgeDeps): () => void {
    // A bridge is replaced when the phase changes, and a logout can fail between the old bridge ending and
    // the new one starting. The last value any bridge saw is kept per app state, so the new bridge still
    // sees the logout that was in flight. It is unset until a bridge has run, so one that starts with no
    // logout in flight never reports a failed one.
    let wasLoggingOut = lastLoggingOut.get(appState)
    return reaction(
        () => ({ expired: sessionState.sessionExpired, loggingOut: appState.isLoggingOut }),
        ({ expired, loggingOut }) => {
            const logoutEnded = wasLoggingOut === true && !loggingOut
            wasLoggingOut = loggingOut
            lastLoggingOut.set(appState, loggingOut)
            if (logoutEnded && (phase === 'unauthenticated' || phase === 'ready') && !expired) {
                send({ type: 'logoutFailed' })
            } else if (loggingOut && (phase === 'ready' || phase === 'expired')) {
                send({ type: 'loggedOut' })
            } else if (expired && phase === 'ready') {
                send({ type: 'sessionExpired' })
            } else if (!expired && phase === 'expired') {
                send({ type: 'sessionRestored' })
            }
        },
        { fireImmediately: true, equals: comparer.structural },
    )
}
